import { blocksClient } from '@/lib/blocks/client';
import { ModuleName } from '@/constant/modules.constants';
import {
  GetFilesPayload,
  GetFilesResponse,
  GetPreSignedUrlForUploadPayload,
  GetPreSignedUrlForUploadResponse,
  StorageFileItem,
} from '../types/storage.types';

const STORAGE_CONFIGURATION_NAME = 'Default';

/** Hrm / template media library folder in Blocks object storage. */
const MEDIA_LIBRARY_PARENT_DIRECTORY_ID =
  import.meta.env.VITE_MEDIA_LIBRARY_PARENT_DIRECTORY_ID ||
  'd9b2d3d4-1331-4e08-9ac5-c346efa6f715';

/** Session cache of file URLs so list/search does not re-hit get-files. */
const fileUrlCache = new Map<string, string>();

const getPreSignedUrlForUpload = async (
  payload: GetPreSignedUrlForUploadPayload
): Promise<GetPreSignedUrlForUploadResponse> => {
  return (await blocksClient.data.files.presignedUploadUrl(
    payload
  )) as GetPreSignedUrlForUploadResponse;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const readString = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

/** Normalize Blocks camelCase / PascalCase file/object rows into one shape. */
const normalizeStorageFileItem = (raw: unknown): StorageFileItem => {
  const row = asRecord(raw) || {};
  const nested = asRecord(row.properties) || asRecord(row.Properties) || {};
  const sizeRaw =
    row.sizeInBytes ??
    row.SizeInBytes ??
    row.size ??
    row.Size ??
    nested.sizeInBytes ??
    nested.size;

  return {
    itemId:
      readString(
        row.itemId,
        row.ItemId,
        row.fileId,
        row.FileId,
        row.id,
        row.Id,
        row.objectId,
        row.ObjectId,
        nested.itemId,
        nested.id
      ) || null,
    url:
      readString(
        row.url,
        row.Url,
        row.URL,
        row.fileUrl,
        row.FileUrl,
        row.downloadUrl,
        row.DownloadUrl,
        row.contentUrl,
        row.ContentUrl,
        row.publicUrl,
        row.PublicUrl,
        nested.url,
        nested.Url,
        nested.fileUrl
      ) || null,
    name:
      readString(
        row.name,
        row.Name,
        row.fileName,
        row.FileName,
        row.artifactName,
        row.ArtifactName,
        row.systemName,
        row.SystemName,
        nested.name,
        nested.fileName
      ) || null,
    parentDirectoryID:
      readString(
        row.parentDirectoryID,
        row.ParentDirectoryID,
        row.parentDirectoryId,
        row.ParentDirectoryId,
        nested.parentDirectoryId
      ) || null,
    systemName: readString(row.systemName, row.SystemName) || null,
    type: typeof row.type === 'number' ? row.type : undefined,
    typeString:
      readString(
        row.typeString,
        row.TypeString,
        row.type,
        row.Type,
        row.contentType,
        row.ContentType,
        row.mimeType,
        row.MimeType,
        row.extension,
        row.Extension,
        nested.mimeType
      ) || null,
    sizeInBytes: typeof sizeRaw === 'number' ? sizeRaw : undefined,
    accessModifier: readString(row.accessModifier, row.AccessModifier) || undefined,
    metaData: (row.metaData ?? row.MetaData) as Record<string, unknown> | null | undefined,
  };
};

const extractObjectRows = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) return payload;
  const row = asRecord(payload);
  if (!row) return [];
  const data =
    row.objects ??
    row.Objects ??
    row.data ??
    row.Data ??
    row.items ??
    row.Items ??
    row.files ??
    row.Files ??
    row.result ??
    row.Result ??
    row.value ??
    row.Value;
  return Array.isArray(data) ? data : [];
};

/**
 * Bulk file details (including public URLs) for the given ids, read from the project's own Blocks
 * Data host — the one that issued the user's token.
 */
export const getFiles = async (
  payload: GetFilesPayload | string[]
): Promise<GetFilesResponse> => {
  const fileIds = (Array.isArray(payload) ? payload : payload.fileIds || [])
    .map((id) => id.trim())
    .filter(Boolean);
  const uniqueIds = [...new Set(fileIds)];

  if (uniqueIds.length === 0) {
    return { data: [], isSuccess: true };
  }

  const response = await blocksClient.data.files.getMany({
    fileIds: uniqueIds,
    configurationName: STORAGE_CONFIGURATION_NAME,
  });

  const row = asRecord(response) || {};
  if (row.errors && typeof row.errors === 'object') {
    const errors = row.errors as Record<string, string>;
    if (Object.keys(errors).length > 0) {
      throw new Error(Object.values(errors).join(', ') || 'Failed to get files.');
    }
  }

  if (row.isSuccess === false) {
    throw new Error('Failed to get files.');
  }

  const data = extractObjectRows(response).map(normalizeStorageFileItem);
  for (const item of data) {
    const id = (item.itemId || '').trim();
    const url = (item.url || '').trim();
    if (id && url) {
      fileUrlCache.set(id, url);
    }
  }

  return { data, isSuccess: true };
};

const putFileToUploadUrl = async (uploadUrl: string, file: File) => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'x-ms-blob-type': 'BlockBlob',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to upload file (${response.status})`);
  }

  return uploadUrl.split('?')[0];
};

const uploadFile = async (
  file: File,
  accessModifier: 'Public' | 'Private',
  moduleName: ModuleName
): Promise<{ fileId: string; fileUrl: string }> => {
  const parentDirectoryId = MEDIA_LIBRARY_PARENT_DIRECTORY_ID;

  const uploadSource = await getPreSignedUrlForUpload({
    name: file.name,
    metaData: '',
    accessModifier,
    configurationName: STORAGE_CONFIGURATION_NAME,
    parentDirectoryId,
    tags: '',
    moduleName,
  });

  if (!uploadSource.isSuccess || !uploadSource.uploadUrl) {
    const errorMessage = Object.values(uploadSource.errors ?? {}).join(', ');
    throw new Error(errorMessage || 'Failed to get upload URL');
  }

  const fileUrl = await putFileToUploadUrl(uploadSource.uploadUrl, file);
  const fileId = uploadSource.fileId ?? '';
  if (fileId && fileUrl) {
    fileUrlCache.set(fileId, fileUrl);
  }

  return { fileId, fileUrl };
};

export const uploadPublicFile = (file: File, moduleName: ModuleName = ModuleName.DefaultCloud) =>
  uploadFile(file, 'Public', moduleName);

/**
 * Uploads a file only signed-in users of the project may read: `getFiles` answers a short-lived signed
 * URL (about 5 minutes), so read it shortly before showing the file instead of storing the URL.
 */
export const uploadPrivateFile = (file: File, moduleName: ModuleName = ModuleName.DefaultCloud) =>
  uploadFile(file, 'Private', moduleName);

